<template>
	<div
		class="grid sm:grid-cols-2 lg:grid-cols-3 gap-16"
	>
		<OrganismBlogFilter
			v-if="filter"
			class="col-span-full"
			:categories="categories as unknown as BlogCategoryType[]"
			:current-category="currentCategory"
		/>
		<MoleculeBlogItem
			v-for="blog in blogItems"
			:key="`blogpost-${blog.id}`"
			:title="blog.templateProps.title"
			:short-description="blog.templateProps.subtitle"
			:tags="blog.templateProps.tags"
			:category="blog.category"
			:url="blog.path"
			:published-at="blog.updatedAt"
			:featured-image="blog.templateProps.featuredImage"
		/>
		<div
			v-if="pagination"
			class="col-span-full flex justify-center"
		>
			<MoleculePagination
				:pagination="{ total: (blogItemTotal as number), limit: limit }"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { BlogCategoryType } from '#base/core/types/BlogCategoryType';
import type { BlogItemType } from '#base/core/types/BlogItemType';
import { useBlogCategories } from '~/composables/useBlogCategories';

const {
	limit = 12,
	filter = true,
	pagination = true,
} = defineProps<{
	limit?: number;
	filter?: boolean;
	pagination?: boolean;
}>();

const route = useRoute();

type BlogItemTypeWithCategory = BlogItemType & { category: BlogCategoryType };

const pageNumber = computed(() => Number(route.query.page ?? 1));

const blogCategories = useBlogCategories();

const {
	data: categories,
} = await blogCategories.get();

const currentCategory = computed(
	() => {
		return categories.value?.find(
			cat => (cat as unknown as BlogCategoryType).path === route.path,
		) as BlogCategoryType | undefined;
	},
);

const blogQuery = queryCollection('blog')
	.where('path', 'LIKE', `${route.path}%`)
	.where('stem', 'NOT LIKE', '%/index');

const {
	data: blogItemTotal,
} = await useAsyncData(`blog-item-count-${route.path}`, () => blogQuery.count());

const {
	data: blogItems,
} = await useAsyncData(
	computed(() => `blog-items-${route.path}-${pageNumber.value}`),
	() => blogQuery
		.skip((pageNumber.value - 1) * limit)
		.order('updatedAt', 'DESC')
		.limit(limit)
		.all(),
	{
		watch: [pageNumber],
		transform: value => value.map((blog) => {
			const category = categories.value?.find(cat => blog.path.includes(cat.path));

			if (!category) {
				throw new Error('Blog item without category');
			}

			return {
				...blog,
				category: category,
				publishedAt: blog.publishedAt,
				updatedAt: blog.updatedAt,
			} as unknown as BlogItemTypeWithCategory;
		}),
	},
);
</script>
